import { createAsyncThunk } from "@reduxjs/toolkit"

import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types.ts"
import {
    IOneVerifyOrganizationUserInvite,
    IOneVerifyPracticeUsageQuotaExtended,
    IOneVerifyProfile,
} from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { AmplitudeHelper } from "../../helpers/amplitude.helper"
import { AuthHelper } from "../../helpers/auth.helper.ts"
import { UserDetailsService } from "../../services/userDetails.service"
import type { IThunkActionData } from "../../types/general.types.ts"
import { authSetCheckUserInvitation } from "../slices/auth.slice"
import {
    runtimeStartLoading,
    runtimeStopLoading,
} from "../slices/runtime.slice"
import { userDetailsSetProfile } from "../slices/userDetails.slice"

/**
 * Get profile
 */
export const userDetailsGetProfile = createAsyncThunk<
    IOneVerifyProfile | undefined,
    IThunkActionData<undefined> | undefined
>("userDetailsGetProfilePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("userDetailsGetProfileLoading"))

    try {
        const result = await UserDetailsService.getProfile()

        if (result) {
            dispatch(userDetailsSetProfile(result))

            const eagleEyeData = AuthHelper.getEagleEyeViewAsData()

            const userId = eagleEyeData?.eagleEyeUserId || result.id

            AmplitudeHelper.identify(`user_${userId}`, {
                practiceId: result.practiceRoles?.[0]?.practice?.id,
                userPracticeId: result.practiceRoles.map(
                    role => role.practice.id,
                ),
                userPracticeName: result.practiceRoles.map(
                    role => role.practice.name,
                ),
                impersonatedUserId: eagleEyeData?.eagleEyeUserId
                    ? result.id
                    : undefined,
            })

            action?.onSuccess?.(result)

            return result
        } else {
            action?.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(runtimeStopLoading("userDetailsGetProfileLoading"))

        action?.onFinally?.()
    }
})

/**
 * Set selected practice role
 */
export const userDetailsAcceptOrganizationInvite = createAsyncThunk<
    void,
    IThunkActionData<IOneVerifyOrganizationUserInvite>
>("userDetailsAcceptOrganizationInvitePrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("userDetailsAcceptOrganizationInviteLoading"))

    try {
        const result = await UserDetailsService.acceptUserInvitation(
            action.payload,
        )

        if (result) {
            dispatch(userDetailsSetProfile(result))
            dispatch(authSetCheckUserInvitation())

            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }
    } catch (e) {
    } finally {
        dispatch(
            runtimeStopLoading("userDetailsAcceptOrganizationInviteLoading"),
        )

        action.onFinally?.()
    }
})

/**
 * Get practice usage quota
 */
export const userDetailsGetPracticeUsageQuota = createAsyncThunk<
    IOneVerifyPracticeUsageQuotaExtended | null,
    IThunkActionData<IPractice>
>("userDetailsGetPracticeUsageQuotaPrefix", async (action, { dispatch }) => {
    dispatch(runtimeStartLoading("userDetailsGetPracticeUsageQuotaLoading"))

    try {
        const result = await UserDetailsService.getPracticeUsageQuota(
            action.payload,
        )

        if (result) {
            action.onSuccess?.(result)
        } else {
            action.onError?.(result)
        }

        return result || null
    } catch (e) {
        return null
    } finally {
        dispatch(runtimeStopLoading("userDetailsGetPracticeUsageQuotaLoading"))

        action.onFinally?.()
    }
})
